import i18n from 'i18next';

/**
 * Message/email template substitution. Pure string ops, no React dependency.
 *
 * Like callUtils, the contact shape uses snake_case because legacy
 * `.jsx` callers pass raw payloads from `/api/entities/Prospect`.
 */

export interface TemplateVariableDoc {
  variable: string;
  label: string;
  description: string;
}

interface TemplateContact {
  prospect_name?: string | null;
  prospect_company?: string | null;
  prospect_phone?: string | null;
  prospect_email?: string | null;
  prospect_title?: string | null;
}

interface TemplateAgentObject {
  caller_name?: string | null;
  agent_name?: string | null;
}

// Labels are resolved at call time so they follow the active language.
const TEMPLATE_VARIABLE_KEYS: Array<[string, string]> = [
  ['[Nom]', 'name'],
  ['[Societe]', 'company'],
  ['[Agent]', 'agent'],
  ['[Telephone]', 'phone'],
  ['[Email]', 'email'],
  ['[Poste]', 'title'],
  ['[Date]', 'date'],
];

export function getTemplateVariables(): TemplateVariableDoc[] {
  return TEMPLATE_VARIABLE_KEYS.map(([variable, key]) => ({
    variable,
    label: i18n.t(`dialer:templateVars.${key}.label`),
    description: i18n.t(`dialer:templateVars.${key}.description`),
  }));
}

export function applyTemplate(
  template: string,
  contact: TemplateContact = {},
  agent: string | TemplateAgentObject = '',
): string {
  if (typeof template !== 'string') return '';

  const agentName =
    typeof agent === 'string'
      ? agent
      : agent?.caller_name || agent?.agent_name || '';

  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;

  return template
    .replace(/\[Nom\]/g, contact.prospect_name || '')
    .replace(/\[Societe\]/g, contact.prospect_company || '')
    .replace(/\[Agent\]/g, agentName)
    .replace(/\[Telephone\]/g, contact.prospect_phone || '')
    .replace(/\[Email\]/g, contact.prospect_email || '')
    .replace(/\[Poste\]/g, contact.prospect_title || '')
    .replace(/\[Date\]/g, formattedDate);
}
